import {InputBase} from "@mui/material";
import {formatDateHM} from "crqlar-react-common/dist/utils/date.util";
import {useEffect, useRef, useState} from "react";
import {CELL_INPUT_SX, FOLIO_CELL_LINE_HEIGHT} from "./FolioCellBox";
import {amountText, FolioFormat, parseAmountText} from "./folioFormat";

/*
    The inputs inside the cell boxes (FolioCellBox draws outline, hover and focus): no label - the
    column header names them - and each one's aria-label says what it is.
*/

interface AmountProps {
    cents?: number
    onChange: (cents: number | undefined) => void
    onBlur?: () => void
    format: FolioFormat
    ariaLabel: string
    placeholder?: string
    error?: boolean
    autoFocus?: boolean
}

/** A price in the folio's currency ("12,50" or "12.50"); text that isn't an amount yet waits for more. */
export const FolioAmountInput = ({cents, onChange, onBlur, format, ariaLabel, placeholder, error, autoFocus}: AmountProps) => {
    const [text, setText] = useState(() => amountText(cents, format.language));
    const focused = useRef(false);

    useEffect(() => {
        if (!focused.current) setText(amountText(cents, format.language));
    }, [cents, format.language]);

    return (
        <InputBase
            value={text}
            error={error}
            autoFocus={autoFocus}
            placeholder={placeholder}
            onFocus={() => {
                focused.current = true;
            }}
            onChange={event => {
                const next = event.target.value;
                setText(next);
                const parsed = parseAmountText(next);
                if (parsed !== undefined || !next.trim()) onChange(parsed);
            }}
            onBlur={() => {
                focused.current = false;
                setText(amountText(cents, format.language));
                onBlur?.();
            }}
            inputProps={{inputMode: 'decimal', 'aria-label': ariaLabel, style: {textAlign: 'right'}}}
            sx={CELL_INPUT_SX}
        />
    );
};

interface IntegerProps {
    value: number
    min: number
    onChange: (value: number) => void
    onBlur?: () => void
    ariaLabel: string
}

/** Whole numbers from [min] up; an empty field keeps the last value. */
export const FolioIntegerInput = ({value, min, onChange, onBlur, ariaLabel}: IntegerProps) => {
    const [text, setText] = useState(String(value));
    const focused = useRef(false);

    useEffect(() => {
        if (!focused.current) setText(String(value));
    }, [value]);

    return (
        <InputBase
            value={text}
            onFocus={() => {
                focused.current = true;
            }}
            onChange={event => {
                const digits = event.target.value.replace(/\D/g, '');
                setText(digits);
                const parsed = parseInt(digits, 10);
                if (!Number.isNaN(parsed) && parsed >= min) onChange(parsed);
            }}
            onBlur={() => {
                focused.current = false;
                setText(String(value));
                onBlur?.();
            }}
            inputProps={{inputMode: 'numeric', 'aria-label': ariaLabel, style: {textAlign: 'right'}}}
            sx={CELL_INPUT_SX}
        />
    );
};

interface TimeProps {
    start?: Date
    onChange: (start: Date) => void
    onBlur?: () => void
    ariaLabel: string
}

/** The line's start time; its date stays. The browser's picker icon is kept small. */
export const FolioTimeInput = ({start, onChange, onBlur, ariaLabel}: TimeProps) => (
    <InputBase
        type={'time'}
        value={start ? formatDateHM(start) : ''}
        onChange={event => {
            const [hours, minutes] = event.target.value.split(':').map(part => parseInt(part, 10));
            if (!start || Number.isNaN(hours) || Number.isNaN(minutes)) return;
            const next = new Date(start);
            next.setHours(hours, minutes, 0, 0);
            onChange(next);
        }}
        onBlur={onBlur}
        inputProps={{step: 300, 'aria-label': ariaLabel}}
        sx={{
            ...CELL_INPUT_SX,
            flex: '0 0 auto',
            '& .MuiInputBase-input': {p: 0, height: FOLIO_CELL_LINE_HEIGHT, lineHeight: `${FOLIO_CELL_LINE_HEIGHT}px`, width: 'auto'},
            '& input::-webkit-calendar-picker-indicator': {m: 0, ml: 0.25, p: 0, width: 12, height: 12, opacity: 0.6},
        }}
    />
);
